/**
 * Enhanced authentication utilities for handling token expiration gracefully
 */

/**
 * Enhanced getTokenAsync that handles token refresh attempts more gracefully
 */
export const getTokenWithRetry = async (maxRetries = 1): Promise<string | null> => {
  // Import the actual getTokenAsync from the authentication module
  const { getTokenAsync } = await import("authentication");

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const token = await getTokenAsync();
      if (token) {
        return token;
      }
    } catch (error) {
      console.warn(`Token retrieval attempt ${attempt + 1} failed:`, error);

      if (attempt === maxRetries) {
        // Final attempt failed - return null silently
        console.debug("Enhanced auth: Token refresh failed after retries, proceeding silently");
        return null;
      }

      // Wait briefly before retry
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }

  return null;
};

/**
 * Check if the current user session is still valid
 */
export const isSessionValid = async (): Promise<boolean> => {
  const token = await getTokenWithRetry(0); // No retries for session check
  return !!token;
};
